import type { SupabaseClient } from "@supabase/supabase-js";

export type VoiceModificationRow = {
  updated_at?: string | null;
  created_at?: string | null;
};

/** Read the catalog event timestamp, not only surviving rows (which omit deletions). */
export async function loadVoiceCatalog<T extends VoiceModificationRow>(client: Pick<SupabaseClient, "from">): Promise<{ rows: T[]; totalCount: number | null; lastModified: string | null }> {
  const voices: T[] = []; const seen = new Set<string>(); let count: number | null = null;
  for (let offset = 0; offset < 100000; offset += 500) {
    const page = await client.from("voices").select("*", { count: "exact" })
      .eq("provider", "gemini").order("display_name", { ascending: true }).order("id", { ascending: true }).range(offset, offset + 499);
    if (page.error) throw page.error;
    if (!Number.isSafeInteger(page.count) || page.count! < 0 || (count !== null && count !== page.count)) throw new Error("Voice catalog changed or count unavailable; refresh.");
    count = page.count;
    for (const row of page.data ?? []) {
      if (!row.id || seen.has(row.id)) throw new Error("Voice catalog changed; refresh.");
      seen.add(row.id); voices.push(row as T);
    }
    if (voices.length === count) break;
    if (!page.data?.length || voices.length > count! || offset >= 99500) throw new Error("Voice catalog is incomplete; refresh.");
  }
  const { data: state, error: stateError } = await client.from("voice_catalog_state")
    .select("last_modified_at").eq("id", true).single();
  if (stateError) throw stateError;
  const timestamp = state?.last_modified_at ? Date.parse(state.last_modified_at) : NaN;
  return { rows: voices, totalCount: count,
    lastModified: Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null };
}

/** Latest saved timestamp among current catalog voices, independent of UI filters. */
export function latestVoiceModification(rows: readonly VoiceModificationRow[]): string | null {
  let latest: number | null = null;
  for (const row of rows) {
    for (const value of [row.updated_at, row.created_at]) {
      if (!value) continue;
      const timestamp = Date.parse(value);
      if (Number.isFinite(timestamp) && (latest === null || timestamp > latest)) latest = timestamp;
    }
  }
  return latest === null ? null : new Date(latest).toISOString();
}
